/*
 * CUSTOMIZED - HYPOGEN | 2026-11-03
 * Purpose: refresh only the cart recommendation section after native cart updates.
 * Maintenance: HYPOGEN - Warenkorbprodukte section; existing Dawn cards and global.js.
 * Dependencies: pubsub.js subscribe/PUB_SUB_EVENTS.cartUpdate and Section Rendering.
 * Read-only GET requests; no cart, checkout, analytics or consent writes.
 * Preserve focus by deferring refresh while it is within this recommendation region.
 * Abort stale requests and unsubscribe on removal. Recheck after theme/app updates.
 * Prepared only; browser acceptance remains open.
 */
if (!customElements.get('hypogen-cart-related')) {
  class HypogenCartRelated extends HTMLElement {
    connectedCallback() {
      if (this.unsubscribeCart) return;
      this.handleFocusOut = () => {
        requestAnimationFrame(() => {
          if (this.pendingRefresh && !this.contains(document.activeElement)) this.refresh();
        });
      };
      this.addEventListener('focusout', this.handleFocusOut);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refresh());
      }
    }

    disconnectedCallback() {
      this.unsubscribeCart?.();
      this.unsubscribeCart = null;
      this.controller?.abort();
      this.removeEventListener('focusout', this.handleFocusOut);
    }

    async refresh() {
      this.controller?.abort();
      if (this.contains(document.activeElement)) {
        this.pendingRefresh = true;
        return;
      }
      this.pendingRefresh = false;
      const controller = new AbortController();
      this.controller = controller;
      this.setAttribute('aria-busy', 'true');
      try {
        const url = new URL(this.dataset.cartUrl, window.location.origin);
        url.searchParams.set('section_id', this.dataset.sectionId);
        const response = await fetch(url.toString(), {
          signal: controller.signal,
          credentials: 'same-origin',
          headers: { Accept: 'text/html' }
        });
        if (!response.ok) throw new Error('Recommendation section response failed');
        const text = await response.text();
        if (controller.signal.aborted || !this.isConnected) return;
        const html = new DOMParser().parseFromString(text, 'text/html');
        const replacement = Array.from(html.querySelectorAll('hypogen-cart-related'))
          .find((element) => element.dataset.sectionId === this.dataset.sectionId);
        if (!replacement) throw new Error('Recommendation section not returned');
        if (this.contains(document.activeElement)) {
          this.pendingRefresh = true;
          return;
        }
        this.replaceChildren(...Array.from(replacement.childNodes));
      } catch (error) {
        if (error.name !== 'AbortError' && !this.contains(document.activeElement)) {
          // Fail closed: no stale product recommendations; checkout stays untouched.
          this.replaceChildren();
        }
      } finally {
        if (this.controller === controller) this.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('hypogen-cart-related', HypogenCartRelated);
}
